import React, { useEffect, useState } from 'react';
import { Dialog, IconButton, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import { FORM_COLORS } from './constants';
import { PublicationFormModalProps } from './types';
import { usePublicationForm } from './hooks/usePublicationForm';
import { usePublicationCatalogs } from './hooks/usePublicationCatalogs';
import { FormStepper } from './components/FormStepper';
import { GeneralTab } from './tabs/GeneralTab';
import { CampaignTab } from './tabs/CampaignTab';
import { PaymentMethodsTab } from './tabs/PaymentMethodsTab';
import { ZoneTab } from './tabs/ZoneTab';
import { StockPricesTab } from './tabs/StockPricesTab';
import { VariantsTab } from './tabs/VariantsTab';

const useStyles = makeStyles(() => ({
  paper: {
    borderRadius: 14,
    width: 880,
    maxWidth: '96vw',
  },
  body: {
    padding: '26px 30px 30px',
    fontFamily: 'inherit',
    color: FORM_COLORS.text,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  title: {
    fontWeight: 500,
    fontSize: 16,
    color: FORM_COLORS.text,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 22,
    paddingTop: 18,
    borderTop: `0.5px solid ${FORM_COLORS.border}`,
  },
  footerRight: {
    display: 'flex',
    gap: 10,
  },
  primaryButton: {
    background: FORM_COLORS.pink,
    color: '#fff',
    border: `1.5px solid ${FORM_COLORS.pink}`,
    borderRadius: 8,
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: 'inherit',
    transition: 'opacity .15s',
    '&:hover': {
      opacity: 0.88,
    },
    '&:disabled': {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
  },
  outlineButton: {
    background: 'transparent',
    color: FORM_COLORS.pink,
    border: `1.5px solid ${FORM_COLORS.pink}`,
    borderRadius: 8,
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: 'inherit',
  },
  ghostButton: {
    background: 'none',
    border: `0.5px solid ${FORM_COLORS.border}`,
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 12,
    cursor: 'pointer',
    color: FORM_COLORS.textSecondary,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    fontFamily: 'inherit',
  },
}));

export const PublicationFormModal = ({
  open,
  marketplace,
  publication,
  onClose,
  onSubmit,
}: PublicationFormModalProps) => {
  const classes = useStyles();
  const [saving, setSaving] = useState(false);
  const {
    values,
    onChange,
    isEditing,
    isValid,
    currentStep,
    totalSteps,
    goToStep,
    goNext,
    goBack,
  } = usePublicationForm({ open, publication, marketplace });

  const {
    products,
    campaignOptions,
    zoneOptions,
    targetOptions,
    paymentCards,
    paymentCardOptions,
    loadingCatalogs,
    loadingTargets,
  } = usePublicationCatalogs({
    open,
    marketplace,
    selectedCampaignIds: values.campaigns,
  });

  useEffect(() => {
    if (!open) return;
    const allTargetIds = targetOptions.map((option) => option.id);
    const targetsMatch =
      allTargetIds.length === values.targets.length &&
      allTargetIds.every((id) => values.targets.includes(id));
    if (!targetsMatch) {
      onChange('targets', allTargetIds);
    }
  }, [open, targetOptions, values.targets, onChange]);

  const isLastStep = currentStep === totalSteps - 1;

  const tabProps = { values, onChange, isEditing };
  const tabs = [
    <GeneralTab
      key="general"
      {...tabProps}
      products={products}
      loading={loadingCatalogs}
      marketplace={marketplace}
    />,
    <CampaignTab
      key="campaign"
      {...tabProps}
      campaignOptions={campaignOptions}
      loading={loadingCatalogs}
    />,
    <PaymentMethodsTab
      key="payment"
      {...tabProps}
      targetOptions={targetOptions}
      paymentCardOptions={paymentCardOptions}
      loading={loadingTargets}
      loadingPaymentCards={loadingCatalogs}
    />,
    <ZoneTab
      key="zone"
      {...tabProps}
      zoneOptions={zoneOptions}
      loading={loadingCatalogs}
    />,
    <StockPricesTab key="stock" {...tabProps} />,
    <VariantsTab key="variants" {...tabProps} />,
  ];

  const handleSubmit = async () => {
    if (!isValid) return;
    try {
      setSaving(true);
      await onSubmit?.(values, isEditing, paymentCards);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const title = isEditing
    ? `Editar — ${values.publicationId} · ${values.sku}`
    : 'Nueva publicación';

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }} maxWidth={false}>
      <div className={classes.body}>
        <div className={classes.header}>
          <span className={classes.title}>{title}</span>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <FormStepper currentStep={currentStep} onStepClick={goToStep} />

        {tabs[currentStep]}

        <div className={classes.footer}>
          {currentStep === 0 ? (
            <button type="button" className={classes.ghostButton} onClick={onClose}>
              Cancelar
            </button>
          ) : (
            <button type="button" className={classes.ghostButton} onClick={goBack}>
              <ArrowBackIcon style={{ fontSize: 15 }} />
              <span>Atrás</span>
            </button>
          )}

          {isLastStep ? (
            <div className={classes.footerRight}>
              <button
                type="button"
                className={classes.primaryButton}
                disabled={!isValid || saving}
                onClick={handleSubmit}
              >
                <SaveOutlinedIcon style={{ fontSize: 16 }} />
                <span>{isEditing ? 'Guardar cambios' : 'Guardar y agregar a cola'}</span>
              </button>
            </div>
          ) : (
            <button
              type="button"
              className={classes.primaryButton}
              disabled={currentStep === 0 && !isValid}
              onClick={goNext}
            >
              <span>Continuar</span>
              <ArrowForwardIcon style={{ fontSize: 15 }} />
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
};
